import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmValueApi, crmValueQueryKeys } from "../api/crmValue";
import {
  CrmValue,
  CrmValueCategory,
  CrmValuePayload,
  CrmValueStatus,
} from "types/crm/values";

/** One tab's worth of values — Lifecycle Stage, Engagement Status or Deal Stage. */
export const useCrmValues = (
  category: CrmValueCategory,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: crmValueQueryKeys.list(category),
    queryFn: () => crmValueApi.getValues(category),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });

/**
 * The Active values a Contact or Deal dropdown may offer.
 *
 * Longer-lived than the settings list: these change only when an admin edits
 * the configuration, and a form that opens twice in a minute should not ask
 * twice. A write to the category invalidates it regardless.
 */
export const useAssignableCrmValues = (
  category: CrmValueCategory,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: crmValueQueryKeys.assignable(category),
    queryFn: () => crmValueApi.getAssignableValues(category),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000,
  });

export interface CrmValueOption {
  value: string;
  label: string;
}

/**
 * A category's values shaped for a `<select>`: the key travels, the name is read.
 *
 * `currentKey` is the value the record already holds. It is appended when the
 * assignable list does not contain it — which happens whenever an admin has
 * deactivated it since, because `assignable` returns Active values only.
 * Without that, opening a record would show an empty field and saving would
 * move it off a value nobody asked to change. The API accepts an unchanged
 * existing value, so it stays selectable; `currentLabel` is the record's own
 * `...Label` field, falling back to the key if the response predates it.
 */
export const useCrmValueOptions = (
  category: CrmValueCategory,
  currentKey?: string,
  currentLabel?: string,
) => {
  const { data: values = [], isLoading } = useAssignableCrmValues(category);

  const options = useMemo<CrmValueOption[]>(() => {
    const assignable = values.map((value) => ({
      value: value.key,
      label: value.name,
    }));

    if (!currentKey || assignable.some((o) => o.value === currentKey)) {
      return assignable;
    }
    return [...assignable, { value: currentKey, label: currentLabel || currentKey }];
  }, [values, currentKey, currentLabel]);

  return { options, isLoading };
};

/**
 * Writes invalidate the category they touched — both its settings list and the
 * dropdowns reading from it, since a rename has to reach the Contact form. The
 * three categories are independent, so none of the others is disturbed.
 *
 * A 404 invalidates too: it means the value is gone, so the row being acted on
 * should stop being there.
 *
 * Errors are not toasted here. A duplicate name belongs in the form that caused
 * it, and the last-active refusal belongs in the deactivate modal, so call
 * sites read `mutation.error.message` and place it themselves.
 */
const useCrmValueWriteCallbacks = () => {
  const queryClient = useQueryClient();

  const invalidate = (category: CrmValueCategory) =>
    queryClient.invalidateQueries({
      queryKey: crmValueQueryKeys.category(category),
    });

  return {
    onSuccess: (_result: unknown, variables: { category: CrmValueCategory }) =>
      invalidate(variables.category),
    onError: (
      error: unknown,
      variables: { category: CrmValueCategory },
    ) => {
      if ((error as { status?: number } | null)?.status === 404) {
        invalidate(variables.category);
      }
    },
  };
};

export const useCreateCrmValueMutation = () => {
  const callbacks = useCrmValueWriteCallbacks();

  return useMutation({
    mutationFn: ({
      category,
      ...payload
    }: CrmValuePayload & { category: CrmValueCategory }): Promise<CrmValue> =>
      crmValueApi.createValue(category, payload),
    ...callbacks,
  });
};

export const useUpdateCrmValueMutation = () => {
  const callbacks = useCrmValueWriteCallbacks();

  return useMutation({
    mutationFn: ({
      category,
      valueUuid,
      ...payload
    }: CrmValuePayload & {
      category: CrmValueCategory;
      valueUuid: string;
    }): Promise<CrmValue> =>
      crmValueApi.updateValue(category, valueUuid, payload),
    ...callbacks,
  });
};

/**
 * The Delete modal's data for one value. Never cached across opens: the count
 * and whether the delete is allowed change as other admins work.
 */
export const useCrmValueUsage = (
  category: CrmValueCategory,
  valueUuid?: string,
) =>
  useQuery({
    queryKey: crmValueQueryKeys.usage(category, valueUuid ?? ""),
    queryFn: () => crmValueApi.getUsage(category, valueUuid as string),
    enabled: Boolean(valueUuid),
    staleTime: 0,
    gcTime: 0,
  });

export const useDeleteCrmValueMutation = () => {
  const callbacks = useCrmValueWriteCallbacks();

  return useMutation({
    mutationFn: ({
      category,
      valueUuid,
      replaceWithUuid,
    }: {
      category: CrmValueCategory;
      valueUuid: string;
      replaceWithUuid?: string;
    }) => crmValueApi.deleteValue(category, valueUuid, replaceWithUuid),
    ...callbacks,
  });
};

export const useUpdateCrmValueStatusMutation = () => {
  const callbacks = useCrmValueWriteCallbacks();

  return useMutation({
    mutationFn: ({
      category,
      valueUuid,
      status,
    }: {
      category: CrmValueCategory;
      valueUuid: string;
      status: CrmValueStatus;
    }): Promise<CrmValue> =>
      crmValueApi.updateValueStatus(category, valueUuid, status),
    ...callbacks,
  });
};
